import * as THREE from 'three';
import { getCoreAppearance } from './coreAppearance';
import { createCorePhaseAttacks } from './corePhaseAttacks';
import { createCoreHitImpact } from './coreHitImpact';

export const CORE_LASER_TIMINGS = {
  bossCharge: 900,
  bossFire: 300,
  bossImpact: 1250,
  bossOutcome: 1700,
};
const REDUCED_TIMINGS = {
  bossCharge: 250,
  bossFire: 150,
  bossImpact: 650,
  bossOutcome: 1200,
};
const CORE_MASSIVE_LASER_TIMINGS = {
  ...CORE_LASER_TIMINGS,
  bossCharge: 1600,
  bossFire: 500,
};
export const getCoreLaserDuration = (
  stage,
  reduceMotion = false,
  attackType = null
) =>
  (reduceMotion
    ? REDUCED_TIMINGS
    : attackType === 'massiveAttack'
    ? CORE_MASSIVE_LASER_TIMINGS
    : CORE_LASER_TIMINGS)[stage];
export const getCoreLaserOutcome = (counter) =>
  counter.protocolSaved
    ? 'protocol'
    : counter.decoyIntercepted
    ? 'decoy'
    : !counter.hit
    ? 'miss'
    : counter.damage > 0
    ? 'hit'
    : 'block';

const glowMaterial = (color, opacity = 1) =>
  new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

export const createCoreLaser = (scene) => {
  const group = new THREE.Group();
  group.name = 'core-counterattack';
  group.visible = false;
  scene.add(group);
  const charge = new THREE.Group();
  const beam = new THREE.Group();
  const impact = new THREE.Group();
  group.add(charge, beam, impact);
  const phaseAttacks = createCorePhaseAttacks(group);
  const hitImpact = createCoreHitImpact(group);
  let impactOccurred = false;
  const rings = [0.4, 0.63, 0.88].map((radius, index) => {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(
        radius,
        0.018 + index * 0.005,
        6,
        48,
        Math.PI * 1.65
      ),
      glowMaterial(index === 1 ? 0xff5fae : 0xcf8aff)
    );
    charge.add(ring);
    return ring;
  });
  const coreGlow = new THREE.Mesh(
    new THREE.SphereGeometry(0.19, 12, 8),
    glowMaterial(0xffd4ff)
  );
  charge.add(coreGlow);
  const layers = [
    [0.025, 0xfff5ff, 0.8],
    [0.075, 0xff4d9d, 0.32],
    [0.16, 0xb563ff, 0.09],
  ].map(([radius, color, opacity]) => {
    const mesh = new THREE.Mesh(
      new THREE.CylinderGeometry(radius, radius, 1, 10),
      glowMaterial(color, opacity)
    );
    beam.add(mesh);
    return mesh;
  });
  const fragments = Array.from({ length: 14 }, (_, index) => {
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(0.025, 0.09, 0.025),
      glowMaterial(index % 2 ? 0xff62af : 0xbf8bff, 0.7)
    );
    beam.add(mesh);
    return mesh;
  });
  const ribbons = Array.from({ length: 3 }, () => {
    const positions = new Float32Array(33 * 2 * 3);
    const indices = [];
    for (let segment = 0; segment < 32; segment += 1) {
      const a = segment * 2;
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3).setUsage(THREE.DynamicDrawUsage)
    );
    geometry.setIndex(indices);
    const mesh = new THREE.Mesh(geometry, glowMaterial(0xff81b3, 0.65));
    mesh.material.side = THREE.DoubleSide;
    mesh.frustumCulled = false;
    beam.add(mesh);
    return mesh;
  });
  const waveGeometry = new THREE.TorusGeometry(1, 0.022, 5, 48);
  const shockRings = Array.from({ length: 4 }, () => {
    const mesh = new THREE.Mesh(waveGeometry, glowMaterial(0xffc34e, 0.4));
    mesh.rotation.x = Math.PI / 2;
    beam.add(mesh);
    return mesh;
  });
  const chargeSpikes = Array.from({ length: 8 }, () => {
    const mesh = new THREE.Mesh(
      new THREE.ConeGeometry(0.045, 0.3, 4),
      glowMaterial(0xffc34e, 0.7)
    );
    charge.add(mesh);
    return mesh;
  });
  const shield = new THREE.Mesh(
    new THREE.IcosahedronGeometry(1.12, 1),
    new THREE.MeshBasicMaterial({
      color: 0x32cfca,
      wireframe: true,
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
  );
  const shieldGlow = new THREE.Mesh(
    new THREE.SphereGeometry(1.1, 16, 12),
    glowMaterial(0x2dbdcc, 0.1)
  );
  impact.add(shield, shieldGlow);
  const shards = Array.from({ length: 24 }, (_, index) => {
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(0.035, 0.1 + (index % 3) * 0.05, 0.02),
      glowMaterial(0xff69b1)
    );
    impact.add(mesh);
    return mesh;
  });
  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.055, 12, 8),
    glowMaterial(0xffa6dc, 0.5)
  );
  group.add(head);

  const decoy = new THREE.Group();
  group.add(decoy);
  const decoyParts = [];
  const addDecoyPart = (geometry, position, index) => {
    const material = new THREE.MeshBasicMaterial({
      color: index % 2 ? 0x9272df : 0x24c9d4,
      wireframe: true,
      transparent: true,
      opacity: 0.65,
      toneMapped: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(position);
    mesh.userData.home = position.clone();
    mesh.userData.drift = new THREE.Vector3(
      Math.cos(index * 2.399) * 0.7,
      0.3 + (index % 4) * 0.14,
      Math.sin(index * 2.399) * 0.3
    );
    decoy.add(mesh);
    decoyParts.push(mesh);
    return mesh;
  };
  addDecoyPart(
    new THREE.IcosahedronGeometry(0.15, 1),
    new THREE.Vector3(0, 0.43, 0),
    0
  );
  addDecoyPart(
    new THREE.CylinderGeometry(0.19, 0.12, 0.43, 6),
    new THREE.Vector3(0, 0.04, 0),
    1
  );
  [-1, 1].forEach((side, index) => {
    const arm = addDecoyPart(
      new THREE.CylinderGeometry(0.045, 0.035, 0.43, 5),
      new THREE.Vector3(side * 0.25, 0, 0),
      index + 2
    );
    arm.rotation.z = side * 0.32;
    const leg = addDecoyPart(
      new THREE.CylinderGeometry(0.055, 0.035, 0.43, 5),
      new THREE.Vector3(side * 0.1, -0.39, 0),
      index + 4
    );
    leg.rotation.z = side * -0.1;
  });
  const decoyBase = new THREE.Mesh(
    new THREE.TorusGeometry(0.3, 0.012, 6, 40),
    glowMaterial(0x79fff2, 0.8)
  );
  decoyBase.rotation.x = Math.PI / 2;
  decoyBase.material.toneMapped = false;
  decoyBase.position.y = -0.64;
  const scanRing = new THREE.Mesh(
    new THREE.TorusGeometry(0.27, 0.008, 4, 32),
    glowMaterial(0x79fff2, 0.6)
  );
  scanRing.rotation.x = Math.PI / 2;
  scanRing.material.toneMapped = false;
  decoy.add(decoyBase, scanRing);
  const endpoint = new THREE.Vector3();
  const forward = new THREE.Vector3();
  const right = new THREE.Vector3();
  const cameraUp = new THREE.Vector3();
  const viewForward = new THREE.Vector3();
  const direction = new THREE.Vector3();
  const tip = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);

  const update = ({
    stage,
    progress,
    origin,
    target = null,
    camera,
    outcome,
    corePhase = 'containment',
    attackType = null,
    reduceMotion = false,
  }) => {
    if (!['bossImpact', 'bossOutcome'].includes(stage)) impactOccurred = false;
    if (stage === 'bossImpact' && outcome === 'hit') impactOccurred = true;
    group.visible =
      ['bossCharge', 'bossFire', 'bossImpact'].includes(stage) ||
      (stage === 'bossOutcome' && impactOccurred);
    if (!group.visible) return;
    const p = THREE.MathUtils.clamp(progress, 0, 1);
    const massive = attackType === 'massiveAttack' && outcome !== 'miss';
    const appearance = massive
      ? {
          ...getCoreAppearance(corePhase),
          attack: 0xff2344,
          accent: 0x3cfff2,
          hot: 0xfff2ef,
        }
      : getCoreAppearance(corePhase);
    group.userData.attackType = massive ? 'massiveAttack' : null;
    const alternateAttack =
      !massive && ['shards', 'rings', 'arcs'].includes(appearance.attackStyle);
    const feral = corePhase === 'meltdown';
    const power = appearance.laserPower * (massive ? 4 : 1);
    forward
      .copy(target || camera.position)
      .sub(origin)
      .normalize();
    right.set(1, 0, 0).applyQuaternion(camera.quaternion);
    if (target) {
      // Aim in world space. Moving the camera must never move the victim.
      right.crossVectors(forward, up).normalize();
      endpoint.copy(target);
      if (outcome === 'miss') endpoint.addScaledVector(right, 1.25);
    } else {
      endpoint
        .copy(camera.position)
        .addScaledVector(
          forward,
          outcome === 'block' || outcome === 'protocol' ? -2.3 : -0.75
        );
    }
    if (!target && outcome === 'miss')
      endpoint.addScaledVector(right, 3.6).addScaledVector(forward, 1.2);
    if (outcome === 'miss') {
      // Continue through the near-miss point. All phase projectiles share this
      // destination, so neither the beam nor its volleys stop beside the die.
      direction.copy(endpoint).sub(origin).normalize();
      endpoint.addScaledVector(direction, 10);
    }
    decoy.visible = outcome === 'decoy' && stage !== 'bossOutcome';
    if (decoy.visible) {
      // Camera-relative placement keeps the hologram in frame on both a
      // portrait phone and a wide desktop. The beam ends at its torso.
      const depth = 3;
      const halfHeight =
        Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * depth;
      camera.getWorldDirection(viewForward);
      cameraUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
      if (target) endpoint.copy(target).addScaledVector(right, 2);
      else
        endpoint
          .copy(camera.position)
          .addScaledVector(viewForward, depth)
          .addScaledVector(right, halfHeight * camera.aspect * 0.55)
          .addScaledVector(cameraUp, halfHeight * 0.18);
      decoy.position.copy(endpoint);
      decoy.quaternion.copy(camera.quaternion);
      decoy.scale.setScalar(target ? 1 : halfHeight * 0.55);
      const breaking = stage === 'bossImpact';
      const fade = breaking ? 1 - p : 1;
      decoyParts.forEach((mesh) => {
        mesh.position.copy(mesh.userData.home);
        if (breaking && !reduceMotion)
          mesh.position.addScaledVector(mesh.userData.drift, p * 1.4);
        mesh.material.opacity = fade * 0.65;
        mesh.scale.setScalar(breaking && !reduceMotion ? 1 - p * 0.7 : 1);
      });
      decoyBase.material.opacity = fade * 0.8;
      scanRing.visible = !breaking;
      scanRing.position.y = reduceMotion ? 0 : -0.5 + p * 0.9;
    }
    charge.position.copy(origin);
    charge.quaternion.copy(camera.quaternion);
    charge.visible = stage === 'bossCharge' || stage === 'bossFire';
    const chargePower = stage === 'bossCharge' ? p : 1 - p;
    rings.forEach((ring, index) => {
      ring.visible = !alternateAttack;
      ring.scale.setScalar(
        (1.8 - chargePower * 0.8) * (massive ? 2.4 : feral ? 1.25 : 1)
      );
      ring.rotation.z =
        index * 2.1 + (reduceMotion ? 0 : p * (index % 2 ? -4 : 5));
      ring.material.opacity = 0.2 + chargePower * 0.7;
      ring.material.color.setHex(
        index === 1 ? appearance.attack : appearance.accent
      );
    });
    chargeSpikes.forEach((spike, index) => {
      spike.visible = feral || massive;
      const angle = (index / 8) * Math.PI * 2 + (reduceMotion ? 0 : p * 1.5);
      const radius = (1.05 - chargePower * 0.4) * (massive ? 2.3 : 1);
      spike.position.set(Math.cos(angle) * radius, Math.sin(angle) * radius, 0);
      spike.rotation.z = angle + Math.PI / 2;
      spike.material.color.setHex(appearance.accent);
      spike.scale.setScalar((0.8 + chargePower * 0.7) * (massive ? 2 : 1));
    });
    coreGlow.visible = !alternateAttack;
    coreGlow.scale.setScalar((0.5 + chargePower * 1.8) * (massive ? 3 : 1));
    coreGlow.material.opacity = chargePower * 0.6;
    coreGlow.material.color.setHex(appearance.hot);

    beam.visible = stage === 'bossFire' || (stage === 'bossImpact' && p < 0.22);
    head.visible = beam.visible && !alternateAttack;
    if (beam.visible) {
      const travel = stage === 'bossFire' ? Math.pow(p, 0.65) : 1;
      tip.lerpVectors(origin, endpoint, travel);
      direction.copy(tip).sub(origin);
      const length = Math.max(0.001, direction.length());
      direction.normalize();
      beam.position.copy(origin).lerp(tip, 0.5);
      beam.quaternion.setFromUnitVectors(up, direction);
      const fade = stage === 'bossImpact' ? Math.max(0, 1 - p / 0.22) : 1;
      layers.forEach((mesh, index) => {
        mesh.visible = !alternateAttack;
        const width = (1 + travel * 0.35) * power * 1.8;
        mesh.scale.set(width, length, width);
        mesh.material.color.setHex(
          [appearance.hot, appearance.attack, appearance.accent][index]
        );
        mesh.material.opacity = [0.8, 0.32, 0.09][index] * fade;
      });
      ribbons.forEach((mesh, ribbon) => {
        mesh.visible = (feral || massive) && (!reduceMotion || ribbon === 0);
        if (!mesh.visible) return;
        const positions = mesh.geometry.attributes.position;
        for (let segment = 0; segment <= 32; segment += 1) {
          const t = segment / 32;
          const angle =
            t * Math.PI * 5 + ribbon * 2.094 - (reduceMotion ? 0 : p * 12);
          const radius = (0.12 + Math.sin(t * Math.PI) * 0.16) * power * 1.8;
          const width = massive ? 0.19 : 0.08;
          [radius - width, radius + width].forEach((edge, side) => {
            positions.setXYZ(
              segment * 2 + side,
              Math.cos(angle) * edge,
              (t - 0.5) * length,
              Math.sin(angle) * edge
            );
          });
        }
        positions.needsUpdate = true;
        mesh.material.color.setHex(
          ribbon % 2 ? appearance.attack : appearance.accent
        );
        mesh.material.opacity = fade * 0.65;
      });
      shockRings.forEach((ring, index) => {
        ring.visible = (feral || massive) && !reduceMotion;
        const travelAlongBeam = (p * 1.8 + index / 4) % 1;
        ring.position.y = (travelAlongBeam - 0.5) * length;
        ring.scale.setScalar((0.18 + travelAlongBeam * 0.25) * power);
        ring.material.color.setHex(appearance.accent);
        ring.material.opacity =
          Math.sin(travelAlongBeam * Math.PI) * fade * 0.55;
      });
      fragments.forEach((mesh, index) => {
        mesh.visible = !alternateAttack;
        const phase = index * 2.399 + p * 9;
        mesh.position.set(
          Math.cos(phase) * 0.12 * power,
          (index / 13 - 0.5) * length,
          Math.sin(phase) * 0.12 * power
        );
        mesh.scale.set(
          massive ? 3 : 1,
          (0.5 + (index % 3) * 0.6) * (massive ? 3 : 1),
          massive ? 3 : 1
        );
        mesh.material.color.setHex(
          index % 2 ? appearance.attack : appearance.accent
        );
        mesh.material.opacity =
          fade * (reduceMotion ? 0.4 : 0.35 + 0.35 * Math.sin(phase));
      });
      head.position.copy(tip);
      head.material.opacity = fade * 0.5;
      head.material.color.setHex(appearance.hot);
      head.scale.setScalar(power * 1.8);
    }
    impact.visible = stage === 'bossImpact' && outcome !== 'miss';
    if (impact.visible) {
      impact.position.copy(endpoint);
      impact.quaternion.copy(camera.quaternion);
      shield.visible = shieldGlow.visible =
        outcome === 'block' || outcome === 'protocol';
      shield.material.color.set(outcome === 'protocol' ? 0xffda72 : 0x32cfca);
      shieldGlow.material.color.set(
        outcome === 'protocol' ? 0xffc44d : 0x2dbdcc
      );
      const fade = Math.max(0, 1 - p);
      const shieldFit = target ? 1 : Math.min(1, camera.aspect) * 0.4;
      shield.scale.setScalar(shieldFit * (1 + Math.sin(p * Math.PI) * 0.12));
      shieldGlow.scale.copy(shield.scale);
      shield.material.opacity = fade * 0.4;
      shieldGlow.material.opacity = fade * 0.06;
      shards.forEach((mesh, index) => {
        mesh.visible = !reduceMotion || index < 6;
        const angle = index * 2.399;
        const radius = p * (0.35 + (index % 5) * 0.12);
        mesh.position.set(
          Math.cos(angle) * radius,
          Math.sin(angle) * radius,
          p * 0.12
        );
        mesh.rotation.z = angle + (reduceMotion ? 0 : p * 3);
        mesh.material.color.set(
          outcome === 'protocol'
            ? 0xffda72
            : outcome === 'block' || outcome === 'decoy'
            ? 0x65fff0
            : appearance.attack
        );
        mesh.material.opacity = fade * 0.7;
      });
    }
    phaseAttacks.update({
      style: massive ? 'laser' : appearance.attackStyle,
      stage,
      progress: p,
      origin,
      endpoint,
      camera,
      outcome,
      appearance,
      reduceMotion,
      nearMiss: !!target,
    });
    const impactElapsed =
      stage === 'bossImpact'
        ? (p * getCoreLaserDuration('bossImpact', reduceMotion, attackType)) /
          1000
        : (getCoreLaserDuration('bossImpact', reduceMotion, attackType) +
            p * getCoreLaserDuration('bossOutcome', reduceMotion, attackType)) /
          1000;
    hitImpact.update({
      active:
        ['bossImpact', 'bossOutcome'].includes(stage) && outcome === 'hit',
      elapsed: impactElapsed,
      position: endpoint,
      camera,
      appearance,
      reduceMotion,
    });
  };
  return {
    group,
    beam,
    charge,
    shield,
    decoy,
    endpoint,
    phaseAttacks,
    hitImpact,
    update,
  };
};
